import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { subscriberAPI } from '../../Api/api';
import { useAppLanguage } from '../../Hooks/useAppLanguage';
import Button from '../../Component/UI/Button/Button';
import './Unsubscribe.css';

/**
 * Landing page for the "Unsubscribe" link in newsletter emails, which arrives as
 * /unsubscribe?email=... — the address is prefilled but still confirmed by the
 * reader, so a link scanner cannot unsubscribe them by fetching the URL.
 */
const Unsubscribe = () => {
    const { t, dir } = useAppLanguage();
    const [searchParams] = useSearchParams();
    const [email, setEmail] = useState('');
    const [status, setStatus] = useState('idle');
    const [message, setMessage] = useState('');

    useEffect(() => {
        const emailParam = searchParams.get('email');
        if (emailParam) {
            setEmail(emailParam);
        }
    }, [searchParams]);

    const handleUnsubscribe = async (event) => {
        event.preventDefault();
        if (!email.trim()) return;

        setStatus('loading');
        setMessage('');

        try {
            const response = await subscriberAPI.unsubscribe(email.trim());
            setStatus('success');
            setMessage(response.data?.message || t('unsubscribe.success'));
        } catch (error) {
            setStatus('error');
            setMessage(error.response?.data?.message || t('unsubscribe.error'));
        }
    };

    return (
        <main className="main">
            <section className="unsubscribe-section section">
                <div className="container" dir={dir}>
                    <div className="unsubscribe-card">
                        {status === 'success' ? (
                            <div>
                                <div className="unsubscribe-success-visual" aria-hidden="true">
                                    <span className="unsubscribe-success-icon">&#10003;</span>
                                </div>
                                <h1 className="unsubscribe-title">{t('unsubscribe.doneTitle')}</h1>
                                <p className="unsubscribe-lead">{message}</p>
                            </div>
                        ) : (
                            <>
                                <h1 className="unsubscribe-title">{t('unsubscribe.title')}</h1>
                                <p className="unsubscribe-lead">{t('unsubscribe.description')}</p>

                                <form className="unsubscribe-form" onSubmit={handleUnsubscribe} noValidate>
                                    <div>
                                        <label className="unsubscribe-label" htmlFor="unsubscribe-email">
                                            {t('unsubscribe.emailLabel')}
                                        </label>
                                        <input
                                            id="unsubscribe-email"
                                            type="email"
                                            className="unsubscribe-input"
                                            placeholder={t('unsubscribe.emailPlaceholder')}
                                            value={email}
                                            onChange={(event) => setEmail(event.target.value)}
                                            required
                                            disabled={status === 'loading'}
                                            autoComplete="email"
                                        />
                                    </div>
                                    <Button
                                        type="submit"
                                        className="unsubscribe-submit"
                                        disabled={status === 'loading' || !email.trim()}
                                    >
                                        {status === 'loading'
                                            ? t('unsubscribe.processing')
                                            : t('unsubscribe.confirm')}
                                    </Button>
                                </form>

                                {status === 'error' && (
                                    <p className="unsubscribe-alert" role="alert">
                                        {message}
                                    </p>
                                )}
                            </>
                        )}
                    </div>
                </div>
            </section>
        </main>
    );
};

export default Unsubscribe;
